import { CurrentUser } from 'types'

const STORAGE_KEY = 'currentUser'
const SESSION_ID_KEY = 'sessionId'
const SERVICES_KEY = 'services'

const generateUuid = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  // fallback for older browsers without crypto.randomUUID (e.g. Safari < 15.4)
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export const getSessionId = (): string => {
  let id = sessionStorage.getItem(SESSION_ID_KEY)
  if (!id) {
    id = generateUuid()
    sessionStorage.setItem(SESSION_ID_KEY, id)
  }
  return id
}


let cachedRaw: string | null = null
let cachedUser: CurrentUser | null = null

export const getCurrentUser = (): CurrentUser | null => {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) {
    cachedRaw = null
    cachedUser = null
    return null
  }
  if (raw === cachedRaw && cachedUser) return cachedUser
  try {
    cachedUser = JSON.parse(raw) as CurrentUser
    cachedRaw = raw
  } catch {
    cachedRaw = null
    cachedUser = null
  }
  return cachedUser
}

export const setCurrentUser = (user: CurrentUser): void => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user))
}

export const clearCurrentUser = (): void => {
  sessionStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(SESSION_ID_KEY)
  sessionStorage.removeItem(SERVICES_KEY)
}

// Hidden services returned by /services at login, e.g. ['USE_AVAIL_BAL_ALWAYS']
export const getStoredServices = (): string[] => {
  try {
    const services = JSON.parse(sessionStorage.getItem(SERVICES_KEY) || '[]')
    return Array.isArray(services) ? services : []
  } catch {
    return []
  }
}

export const setStoredServices = (services: string[]): void => {
  sessionStorage.setItem(SERVICES_KEY, JSON.stringify(services))
}

export const EMPTY_CURRENT_USER: CurrentUser = {
  dateOfBirth: '',
  gender: '' as CurrentUser['gender'],
  firstName: '',
  surnames: '',
  nationalId: '',
  email: '',
  phoneNumber: '',
  customerNumber: '',
  customerId: '',
  language: '' as CurrentUser['language'],
  migrationStatus: '',
  account: [],
  token: {} as CurrentUser['token'],
  selfieAttachment: '',
  kycType: '' as CurrentUser['kycType'],
  applicationStatus: '' as CurrentUser['applicationStatus'],
  channels: [],
  customerBillers: {
    categoryName: '',
    categoryIcon: '',
    billers: [],
  },
  defaultAccount: '',
  resettingPin: false,
  migratingFromUssdToMobile: '',
  migrated: false,
  resettingPassword: false,
  resettingPasscode: false,
  userId: '',
  nationality: '',
  district: '',
  province: '',
  sector: '',
  profileId: '',
  oldIbUsernameChangedAt: null,
  oldIbUsername: '',
  customerInfo: {
    displayName: '',
    customerType: '',
    customerNumber: '',
    tin: '',
  },
}

export const sortAccountsRwfCurrentFirst = (accounts: CurrentUser['account']): CurrentUser['account'] => {
  if (!accounts || accounts.length === 0) return []
  const sorted = [...accounts]
  const idx = sorted.findIndex(
    (acc) =>
      acc.productGroupId?.toLowerCase().includes('current') &&
      acc.currency?.toUpperCase() === 'RWF'
  )
  if (idx > 0) {
    const [primary] = sorted.splice(idx, 1)
    sorted.unshift(primary)
  }
  return sorted
}
